import { useState, useEffect } from "react";
import { Check, X, Filter, Info } from "lucide-react";

const FilterBrandModal = ({
  isOpen,
  onClose,
  brands,
  selectedBrandIds,
  onApplyFilters,
  getTotalProductCount,
  filteredBrands,
}) => {
  const [tempSelectedBrandIds, setTempSelectedBrandIds] = useState([]);

  // Initialize temp state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedBrandIds([...selectedBrandIds]);
    }
  }, [isOpen, selectedBrandIds]);

  if (!isOpen) return null;

  const handleBrandToggle = (brandId) => {
    setTempSelectedBrandIds((prev) => {
      if (prev.includes(brandId)) {
        return prev.filter((id) => id !== brandId);
      } else {
        return [...prev, brandId];
      }
    });
  };

  const handleSelectAll = () => {
    setTempSelectedBrandIds(brands.map((brand) => brand.id));
  };

  const handleClearAll = () => {
    setTempSelectedBrandIds([]);
  };

  const handleApplyFilters = () => {
    onApplyFilters(tempSelectedBrandIds);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedBrandIds([...selectedBrandIds]); // Reset to original state
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center p-4">
      <div className="filter-popup bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-[#B8460E]" />
              <h3 className="text-lg font-semibold text-white">
                Filter by Brands
              </h3>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-400 mt-2">
            {getTotalProductCount()} products from {filteredBrands.length} brands
          </p>
        </div>

        {/* Filter Actions */}
        <div className="p-4 border-b border-white/10">
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="flex-1 bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium"
            >
              Select All
            </button>
            <button
              onClick={handleClearAll}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Brand List */}
        <div className="p-4">
          <div className="space-y-2">
            {brands.map((brand) => (
              <label
                key={brand.id}
                className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg cursor-pointer transition-colors"
              >
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={tempSelectedBrandIds.includes(brand.id)}
                    onChange={() => handleBrandToggle(brand.id)}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                      tempSelectedBrandIds.includes(brand.id)
                        ? "bg-[#B8460E] border-[#B8460E]"
                        : "border-white/30 hover:border-white/50"
                    }`}
                  >
                    {tempSelectedBrandIds.includes(brand.id) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-white text-sm font-medium">
                      {brand.name}
                    </div>
                    {brand.description && (
                      <div className="relative group">
                        <Info className="w-4 h-4 text-gray-400 hover:text-[#B8460E] transition-colors cursor-help" />
                        {/* Tooltip */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-3 py-2 bg-gray-800/95 backdrop-blur-xl border border-white/10 rounded-lg text-xs text-white w-64 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999]">
                          <div className="text-left">{brand.description}</div>
                          {/* Tooltip Arrow */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-800/95"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {brand.products.length} product
                    {brand.products.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg transition-all duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyFilters}
              className="flex-1 bg-[#B8460E] hover:bg-[#8B3A0E] text-white px-4 py-3 rounded-lg transition-all duration-200 font-medium"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBrandModal;